import React from 'react';
import { LinkProps } from '../../../types/DesignSystem/Link';
import { ExternalLink } from '../Iconography/Icons';
import { withClassName } from '../cn';

/**
 * Link (§ 213) — the one anchor spelling. Accent ink, ALWAYS underlined:
 * in body text a colour-only link is invisible to anyone who cannot see the
 * hue (WCAG 1.4.1), and our accent measures under 3:1 against `ink`, so the
 * underline is load-bearing, not decorative. Hover moves ink AND thickens the
 * underline — two channels, same reason.
 *
 * Client-side routing rides the `as` seam (`as={RouterLink} to="…"`): the
 * design system cannot import the router (`dependencyDirection`), and wrapping
 * a router Link AROUND this would nest anchors.
 */
const Link = React.forwardRef<HTMLAnchorElement, LinkProps>(function Link({
  children,
  external = false,
  as = 'a',
  className = '',
  'data-testid': dataTestId,
  ...rest
}, ref) {
  // Plain destructure + alias, the Typography spelling — the unread-prop scan
  // reads the PROP name for unquoted keys, so `as: Tag` registers as inert.
  const Tag = as;
  return (
    <Tag
      /* rest first — the Button.tsx rule; `href`/`to`/handlers ride through. */
      {...rest}
      {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : {})}
      ref={ref}
      data-testid={dataTestId}
      className={withClassName(`tw-text-accent tw-underline tw-underline-offset-2 tw-decoration-1
        motion-safe:tw-transition-colors motion-safe:tw-duration-fast motion-safe:tw-ease-swift hover:tw-text-accent-hover hover:tw-decoration-2
        focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-focus`, className)}
    >
      {children}
      {external && (
        <>
          <ExternalLink
            size={12}
            aria-hidden="true"
            className="tw-inline-block tw-ml-1 tw-align-[-1px]"
          />
          {/* Announced, not sprung: the glyph is hidden, the words are not. */}
          <span className="tw-sr-only"> (opens in new tab)</span>
        </>
      )}
    </Tag>
  );
});

export default Link;
